import { useCallback, useEffect, useRef, useState } from 'react';

type BroadcastMessage = {
  payload: Record<string, unknown>;
  type: string;
};

/**
 * This tab's id: a BroadcastChannel delivers to every other instance of the channel, those of the
 * sending tab included, and a tab must not act twice on what it did itself (sign in, sign out).
 * Not `crypto.randomUUID()`: plain http on a LAN address (a phone testing the dev server) lacks it.
 */
const THIS_TAB = Math.random().toString(36).slice(2);

const useBroadcastChannel = (channelName: string) => {
  const [message, setMessage] = useState<BroadcastMessage>();
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!('BroadcastChannel' in self)) {
      console.warn('BroadcastChannel is not supported in this browser.');
      return;
    }

    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<BroadcastMessage & { tab?: string }>) => {
      if (event.data.tab === THIS_TAB) return;
      setMessage(event.data);
    };

    // Clean up the channel when the component unmounts
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [channelName]);

  const sendBroadcast = useCallback((msg: BroadcastMessage) => {
    channelRef.current?.postMessage({ ...msg, tab: THIS_TAB });
  }, []);

  return { broadcastMessage: message, sendBroadcast };
};

export default useBroadcastChannel;
